import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Download, ShieldCheck } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { StatusBadge, fmtDate, inputCls } from "./AFields";

const STATUSES = ["", "initiated", "pending", "successful", "failed", "refunded"];

function PaymentTransactions() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const load = () => {
    api.get(`/payments/transactions?q=${encodeURIComponent(q)}&status=${status}`)
      .then((r) => setEvents(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  return (
    <div data-testid="payment-transactions">
      <p className="rounded-lg bg-brandgreen-100/60 px-4 py-2.5 text-xs text-slate-600"><ShieldCheck size={13} className="mr-1 inline" />
        Read-only view of signature-verified Cashfree webhook events. Payment status is set only by the verified webhook — never by the browser.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Order ID / Payment ID…" className={`${inputCls} !w-64`} data-testid="txn-search" />
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy" data-testid="txn-search-btn">Search</button>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !w-44`} data-testid="txn-status-filter">
          {["", "PAID", "FAILED", "USER_DROPPED"].map((s) => <option key={s} value={s}>{s || "All events"}</option>)}
        </select>
      </div>
      <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Event</th><th className="px-4 py-3">Order ID</th><th className="px-4 py-3">CF Payment ID</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Verified Status</th><th className="px-4 py-3">Payment Time</th><th className="px-4 py-3">Method</th><th className="px-4 py-3">Donation</th></tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className="border-b border-gray-100 last:border-0" data-testid={`txn-row-${e.id}`}>
                <td className="px-4 py-3 text-xs font-semibold text-navy">{e.event_type}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.order_id}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.cf_payment_id || "—"}</td>
                <td className="px-4 py-3 font-bold text-navy">{e.amount != null ? `₹${Number(e.amount).toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-4 py-3"><StatusBadge value={e.payment_status || e.event_type} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(e.payment_time || e.received_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{e.payment_method ? Object.keys(JSON.parse(e.payment_method))[0] : "—"}</td>
                <td className="px-4 py-3 text-xs">{e.donation ? <span className="font-semibold text-navy">{e.donation.don_no} · {e.donation.name} · {e.donation.status}{e.donation.receipt_no ? ` · ${e.donation.receipt_no}` : ""}</span> : <span className="text-slate-400">unmatched</span>}</td>
              </tr>
            ))}
            {events.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No verified payment events yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [resendNo, setResendNo] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/payments/email-settings").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!cfg) return <p className="py-8 text-center text-sm text-slate-400">Loading…</p>;
  const save = async () => {
    setBusy("save");
    try { await api.put("/payments/email-settings", { ...cfg.toggles, admin_email: cfg.admin_email }); toast.success("Email settings saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const sendTest = async () => {
    setBusy("test");
    try { await api.post("/payments/email-test", { to: testTo }); toast.success(`Test email sent to ${testTo || cfg.admin_email || cfg.fallback_admin_email}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const resend = async () => {
    setBusy("resend");
    try { await api.post("/payments/resend-receipt", { don_no: resendNo }); toast.success("Receipt email resent to the donor"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const TOGGLES = [["initiated", "Transaction Initiated Email", "Sent to the donor when a Cashfree order is created (before payment — explicitly not a receipt)"],
                   ["success", "Successful Donation Email", "Sent only after verified PAID reconciliation, with receipt link"],
                   ["failed", "Failed Payment Email", "Sent only when Cashfree definitively reports FAILED"],
                   ["receipt", "Receipt Email", "Donor-facing Email Receipt button on receipt pages"]];
  return (
    <div className="space-y-6" data-testid="email-settings">
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <p className="text-sm font-bold text-navy">Donation Email Notifications</p>
        <p className="mt-1 text-xs text-slate-500">SMTP status: {cfg.smtp_configured ? <span className="font-bold text-emerald-700">configured</span> : <span className="font-bold text-amber-700">not configured — emails will not send until SMTP is set up in Integrations</span>}. Passwords are never shown here.</p>
        <div className="mt-4 space-y-3">
          {TOGGLES.map(([k, label, hint]) => (
            <label key={k} className="flex items-start gap-3 rounded-md border border-gray-100 bg-navy-50/40 px-4 py-3" data-testid={`email-toggle-row-${k}`}>
              <input type="checkbox" checked={!!cfg.toggles[k]} onChange={(e) => setCfg({ ...cfg, toggles: { ...cfg.toggles, [k]: e.target.checked } })} className="mt-0.5 h-4 w-4 accent-saffron-500" data-testid={`email-toggle-${k}`} />
              <span><span className="block text-sm font-semibold text-navy">{label}</span><span className="block text-xs text-slate-500">{hint}</span></span>
            </label>
          ))}
        </div>
        <div className="mt-4">
          <label className="mb-1 block text-xs font-semibold text-navy">Admin Notification Email (receives a copy for every verified successful donation)</label>
          <input value={cfg.admin_email} onChange={(e) => setCfg({ ...cfg, admin_email: e.target.value })} placeholder={cfg.fallback_admin_email || "admin@example.org"} className={`${inputCls} !w-80`} data-testid="email-admin-email" />
          <p className="mt-1 text-[11px] text-slate-400">Leave blank to use the site contact email ({cfg.fallback_admin_email || "not set"}).</p>
        </div>
        <button onClick={save} disabled={busy === "save"} className="btn-primary mt-4 !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="email-save-btn">Save Email Settings</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <p className="text-sm font-bold text-navy">Send Test Email</p>
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={cfg.admin_email || cfg.fallback_admin_email || "recipient@example.org"} className={`${inputCls} mt-2`} data-testid="email-test-to" />
          <button onClick={sendTest} disabled={busy === "test"} className="btn-dark mt-3 !rounded-md !px-5 !py-2 text-xs disabled:opacity-50" data-testid="email-test-btn">{busy === "test" ? "SENDING…" : "SEND TEST EMAIL"}</button>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <p className="text-sm font-bold text-navy">Resend Receipt Email</p>
          <input value={resendNo} onChange={(e) => setResendNo(e.target.value)} placeholder="SSF-DON-2026-0001" className={`${inputCls} mt-2`} data-testid="email-resend-no" />
          <button onClick={resend} disabled={busy === "resend" || !resendNo.trim()} className="btn-dark mt-3 !rounded-md !px-5 !py-2 text-xs disabled:opacity-50" data-testid="email-resend-btn">{busy === "resend" ? "SENDING…" : "RESEND RECEIPT EMAIL"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Donations() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [view, setView] = useState("donations");

  const load = () => {
    api.get(`/admin/donations/list?q=${encodeURIComponent(q)}&status=${status}`)
      .then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const update = async (d, body) => {
    try {
      const r = await api.put(`/admin/donations/${d.id}`, body);
      setItems(items.map((i) => (i.id === d.id ? r.data : i)));
      toast.success("Updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const total = items.filter((d) => d.status === "successful").reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div data-testid="admin-donations">
      <SEO title="Donations" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Donations</h1>
          <p className="mt-1 text-sm text-slate-500">Successful total: <b className="text-navy">₹{total.toLocaleString("en-IN")}</b></p>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        {[["donations", "Donations"], ["transactions", "Payment Transactions"], ["emails", "Email Settings"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`donations-view-${k}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${view === k ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>{l}</button>
        ))}
      </div>
      {view === "transactions" ? <div className="mt-4"><PaymentTransactions /></div> : view === "emails" ? <div className="mt-4"><EmailSettings /></div> : (<>
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Name / Ref…" className={`${inputCls} !w-64 !pl-10`} data-testid="donations-search" />
        </div>
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy" data-testid="donations-search-btn">Search</button>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !w-44`} data-testid="donations-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>
      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Ref</th><th className="px-4 py-3">Donor</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Purpose</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Cashfree Order / Payment</th><th className="px-4 py-3">Verified Status</th><th className="px-4 py-3">Txn Ref</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Receipt</th></tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b border-gray-100 last:border-0 hover:bg-navy-50/30" data-testid={`donation-row-${d.id}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{d.don_no}</td>
                <td className="px-4 py-3"><p className="font-semibold text-navy">{d.name}</p><p className="text-xs text-slate-400">{d.mobile} {d.email}</p></td>
                <td className="px-4 py-3 font-bold text-navy">₹{Number(d.amount).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">{d.purpose}</td>
                <td className="px-4 py-3">{fmtDate(d.created_at)}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-600" data-testid={`donation-cf-${d.id}`}>
                  {d.cf_order_id ? <>{d.cf_order_id}<br /><span className="text-slate-400">{d.cf_payment_id || "—"}</span></> : "—"}
                </td>
                <td className="px-4 py-3" data-testid={`donation-paystatus-${d.id}`}>
                  {d.payment_status ? (
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${d.payment_status === "PAID" ? "bg-brandgreen-100 text-brandgreen" : d.payment_status === "REVIEW_REQUIRED" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                      {d.payment_verified && <ShieldCheck size={10} className="mr-0.5 inline" />}{d.payment_status.replaceAll("_", " ")}
                    </span>
                  ) : <span className="text-xs text-slate-400">PENDING</span>}
                </td>
                <td className="px-4 py-3">
                  <input defaultValue={d.txn_ref || ""} onBlur={(e) => e.target.value !== (d.txn_ref || "") && update(d, { txn_ref: e.target.value })}
                    placeholder="—" className="w-32 rounded-md border border-gray-200 px-2 py-1 text-xs" data-testid={`donation-txn-${d.id}`} />
                </td>
                <td className="px-4 py-3">
                  <select value={d.status} onChange={(e) => update(d, { status: e.target.value })} className="rounded-md border border-gray-200 px-2 py-1 text-xs font-semibold" data-testid={`donation-status-${d.id}`}>
                    {STATUSES.filter(Boolean).map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  {d.status === "successful" && d.receipt_no ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/donations/${d.id}/receipt`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy hover:border-saffron-500" data-testid={`donation-receipt-${d.id}`}>
                      <Download size={13} /> {d.receipt_no}
                    </a>
                  ) : <StatusBadge value={d.status} />}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">No donations found.</td></tr>}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}
